import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { initializeFirestore, memoryLocalCache, type Firestore } from 'firebase/firestore'
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging'
import { getStorage, type FirebaseStorage } from 'firebase/storage'

const requiredFirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID

const firebaseConfig: FirebaseOptions = {
  ...requiredFirebaseConfig,
  ...(measurementId ? { measurementId } : {}),
}

const hasAllFirebaseConfig = Object.values(requiredFirebaseConfig).every(Boolean)

if (!hasAllFirebaseConfig) {
  console.warn(
    'Firebase is not fully configured. Populate VITE_FIREBASE_* environment values before using Firestore.',
  )
}

export const firebaseApp: FirebaseApp | null = hasAllFirebaseConfig
  ? initializeApp(firebaseConfig)
  : null

export const auth: Auth | null = firebaseApp
  ? getAuth(firebaseApp)
  : null

export const firestore: Firestore | null = firebaseApp
  ? initializeFirestore(firebaseApp, {
      localCache: memoryLocalCache(),
    })
  : null

export const storage: FirebaseStorage | null = firebaseApp
  ? getStorage(firebaseApp)
  : null

let messaging: Messaging | null = null
let messagingSupportCheck: Promise<Messaging | null> | null = null

export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (messaging) {
    return messaging
  }

  if (!firebaseApp || typeof window === 'undefined' || !('Notification' in window)) {
    return null
  }

  messagingSupportCheck ??= isSupported()
    .then((supported) => {
      if (!supported) {
        return null
      }

      messaging = getMessaging(firebaseApp)
      return messaging
    })
    .catch(() => null)

  return messagingSupportCheck
}
